import produce from 'immer'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  EndscreenConfigApiDTO,
  InfocardConfigApiDTO,
  InfocardsConfigApiDTO,
  YoutubeVideosAttributesApiDTO,
} from '../codegen/generated/Api.schemas'
import { VideoPromotionHelper } from '../helpers/VideoPromotion.helper'
import { YoutubeChannel } from '../types/YoutubeChannel'
import {
  INewPromotionVideoElementsConfiguration,
  INewPromotionVideoSelectStaticFilters,
  IPromotionCopySource,
  IPromotionElementMode,
  IPromotionEndscreenSlotChoice,
  IPromotionInfoCard,
  NewPromotionActions,
  NewPromotionLayoutId,
} from '../types/components/VideoPromotion.type'

const KEY = 'lx-video-promotion'

export type VideoPromotionStore = {
  selectionVideoStaticFilters: INewPromotionVideoSelectStaticFilters | undefined
  elementsConfiguration: INewPromotionVideoElementsConfiguration
  endScreenLayoutId: NewPromotionLayoutId | undefined
  endScreenSlotChoices: IPromotionEndscreenSlotChoice[]
  infoCardsDraft: IPromotionInfoCard[]
  selectedVideos: YoutubeVideosAttributesApiDTO[]
  youtubeVideoIds: YoutubeVideosAttributesApiDTO['id'][]
  youtubeChannelId: YoutubeChannel['id'] | undefined
  videosSyncedAt: number | undefined
  setYoutubeChannelId: (youtubeChannelId: YoutubeChannel['id'] | undefined) => void
  setSelectionVideoStaticFilters: (filters: INewPromotionVideoSelectStaticFilters) => void
  updateVideosSelection: (
    loadedVideos: YoutubeVideosAttributesApiDTO[],
    selectedIds: Set<YoutubeVideosAttributesApiDTO['id']>,
  ) => void
  selectVideos: (videos: YoutubeVideosAttributesApiDTO[]) => void
  clearVideosSelection: () => void
  toggleElementsConfig: (el: NewPromotionActions) => void
  setElementMode: (el: NewPromotionActions, mode: IPromotionElementMode) => void
  setElementCopySource: (el: NewPromotionActions, source: IPromotionCopySource) => void
  setEndscreenLayoutId: (layoutId: NewPromotionLayoutId) => void
  setEndscreenSlotChoice: (slotIndex: number, choice: IPromotionEndscreenSlotChoice) => void
  setEndscreenConfig: (config: EndscreenConfigApiDTO | undefined) => void
  setInfoCardsDraft: (cards: IPromotionInfoCard[]) => void
  reset: () => void
}

export const useVideoPromotionStore = create(
  persist<VideoPromotionStore>(
    (set, _, store) => ({
      selectionVideoStaticFilters: {},
      elementsConfiguration: {
        endScreen: { active: false, mode: 'custom', copySource: undefined, config: undefined },
        infoCards: { active: false, mode: 'custom', copySource: undefined },
      },
      endScreenLayoutId: undefined,
      endScreenSlotChoices: [],
      infoCardsDraft: [],
      selectedVideos: [],
      youtubeVideoIds: [],
      youtubeChannelId: undefined,
      videosSyncedAt: undefined,
      setYoutubeChannelId: youtubeChannelId =>
        set(state =>
          state.youtubeChannelId === youtubeChannelId
            ? state
            : {
                youtubeChannelId,
                selectedVideos: [],
                youtubeVideoIds: [],
                selectionVideoStaticFilters: {
                  ...state.selectionVideoStaticFilters,
                  playlistId: undefined,
                },
              },
        ),
      setSelectionVideoStaticFilters: filters =>
        set(state => {
          const next = { ...state.selectionVideoStaticFilters, ...filters }
          const previous = state.selectionVideoStaticFilters
          const unchanged = (['search', 'videoId', 'playlistId'] as const).every(
            key => (next[key] || undefined) === (previous?.[key] || undefined),
          )
          return unchanged ? state : { selectionVideoStaticFilters: next }
        }),
      updateVideosSelection: (loadedVideos, selectedIds) =>
        set(state => {
          const loadedIds = new Set(loadedVideos.map(v => v.id))
          const selectedVideos = [
            ...state.selectedVideos.filter(v => !loadedIds.has(v.id)),
            ...loadedVideos.filter(v => selectedIds.has(v.id)),
          ]
          return { selectedVideos, youtubeVideoIds: selectedVideos.map(v => v.id) }
        }),
      selectVideos: videos =>
        set(state => {
          const alreadySelected = new Set(state.youtubeVideoIds)
          const selectedVideos = [
            ...state.selectedVideos,
            ...videos.filter(v => !alreadySelected.has(v.id)),
          ]
          return { selectedVideos, youtubeVideoIds: selectedVideos.map(v => v.id) }
        }),
      clearVideosSelection: () => set({ selectedVideos: [], youtubeVideoIds: [] }),
      toggleElementsConfig: el =>
        set(
          produce(state => {
            state.elementsConfiguration[el].active = !state.elementsConfiguration[el].active
          }),
        ),
      setElementMode: (el, mode) =>
        set(
          produce<VideoPromotionStore>(state => {
            state.elementsConfiguration[el].mode = mode
          }),
        ),
      setElementCopySource: (el, source) =>
        set(
          produce<VideoPromotionStore>(state => {
            state.elementsConfiguration[el].copySource = source
          }),
        ),
      setEndscreenLayoutId: layoutId =>
        set(
          produce<VideoPromotionStore>(state => {
            state.endScreenLayoutId = layoutId
            const slotCount = VideoPromotionHelper.LAYOUTS.find(l => l.id === layoutId)?.slots
              .length
            state.endScreenSlotChoices = state.endScreenSlotChoices.slice(0, (slotCount ?? 1) - 1)
          }),
        ),
      setEndscreenSlotChoice: (slotIndex, choice) =>
        set(
          produce<VideoPromotionStore>(state => {
            state.endScreenSlotChoices[slotIndex] = choice
            const layoutId = state.endScreenLayoutId
            if (!layoutId) {
              return
            }
            const layout = VideoPromotionHelper.LAYOUTS.find(l => l.id === layoutId)
            if (!layout) {
              return
            }
            const previous = state.elementsConfiguration.endScreen.config
            const start = previous?.mode === 'custom' ? previous.startSecondsBeforeEnd : 20
            const duration = previous?.mode === 'custom' ? previous.durationSeconds : undefined
            const config = VideoPromotionHelper.buildEndscreenConfig(
              layout,
              state.endScreenSlotChoices,
              start,
              duration,
            )
            state.elementsConfiguration.endScreen.config = config
          }),
        ),
      setEndscreenConfig: config =>
        set(
          produce<VideoPromotionStore>(state => {
            state.elementsConfiguration.endScreen.config = config
          }),
        ),
      setInfoCardsDraft: cards =>
        set(
          produce<VideoPromotionStore>(state => {
            state.infoCardsDraft = cards
          }),
        ),
      reset: () => {
        localStorage.removeItem(KEY)
        set(() => store.getInitialState())
      },
    }),
    { name: KEY, version: 1 },
  ),
)

const toSeconds = (time: string): number | undefined => {
  const parts = time.split(':').map(Number)
  if (parts.length !== 3 || parts.some(p => Number.isNaN(p))) {
    return undefined
  }
  return parts[0] * 3600 + parts[1] * 60 + parts[2]
}

export const selectEndscreenConfig = (
  state: VideoPromotionStore,
): EndscreenConfigApiDTO | undefined => {
  const { active, mode, copySource, config } = state.elementsConfiguration.endScreen
  if (!active) {
    return undefined
  }
  if (mode === 'copyFromVideo') {
    return copySource?.videoId
      ? { mode: 'copyFromVideo', sourceVideoId: copySource.videoId }
      : undefined
  }
  return config
}

export const selectInfocardsConfig = (
  state: VideoPromotionStore,
): InfocardsConfigApiDTO | undefined => {
  const { active, mode, copySource } = state.elementsConfiguration.infoCards
  if (!active) {
    return undefined
  }
  if (mode === 'copyFromVideo') {
    return copySource?.videoId
      ? { mode: 'copyFromVideo', sourceVideoId: copySource.videoId }
      : undefined
  }
  if (state.infoCardsDraft.length === 0 || state.infoCardsDraft.some(ic => !ic.target.targetId)) {
    return undefined
  }
  const cards: InfocardConfigApiDTO[] = state.infoCardsDraft.map(ic => ({
    customMessage: ic.customMessage || undefined,
    teaserText: ic.teaserText || undefined,
    teaserStartOffsetSeconds: toSeconds(ic.teaserStartOffsetSeconds),
    ...(ic.target.type === 'video'
      ? { type: 'video', videoId: ic.target.targetId }
      : { type: 'playlist', playlistId: ic.target.targetId }),
  }))
  return { mode: 'custom', cards }
}
